import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { Input } from '@ami/react/core';
import {
  QuoteProductDto,
  quoteProductLeadTimeType,
} from '../../quotes/otherQuoteEntities';
import { formatPrice } from '../../quotes/priceFormatter';
import { Spinner } from '@ami/react/core';
import { makeConcealable } from '@ami/react/core';
import { ProductGroupMarketingComponent } from '../../products/components/ProductGroupMarketingComponent';
import { ProductConfiguration } from '../../products/productConfiguration';
import { newLineString } from '@ami/react/core';
import EffectiveLeadTimeLabel from '../../quotes/lead-times/EffectiveLeadTimeLabel';
//import './ProductDetailComponent.scss';
import { productGroupDimensionCategory } from '../../products/productGroupDimensionCategoryType';
import { appConfig } from '@ami/react/core';
import { LeadTimesBanner } from '../PricingAndLeadTimesBanner';

const DetailImage = ({ imageUrl }) => <img src={imageUrl} alt="..." />;

const PriceCalculationComponent = ({ title, price, showPricingSpinner, maskPrice }) => (
  <div className="col-4 ps-1 pe-1">
    <div className="pg-price-total-heading">{title}</div>
    <div className="pg-price-total-heading">
      {showPricingSpinner && <i className="fa fa-spin fa-spinner" />}
      {!showPricingSpinner && <div>
        {!maskPrice && <strong>{formatPrice(price)}</strong>}
        {maskPrice && <strong>N/A</strong>}
      </div>
      }
    </div>
  </div>
);

const PricesCalculationComponent = ({ leadTime, productCalculation, showPricingSpinner, showLeadTimeCalculationSpinner, showPricingAndLeadTimesBanner, maskPricingAndLeadTimes }) => (
  <div className="container ms-2 me-2">
    <div className="hr-divider inverse mb-2">
      <h4 className="hr-divider-content hr-divider-heading">As Configured</h4>
    </div>
    <div className="row justify-content-center">
      {showPricingAndLeadTimesBanner && <LeadTimesBanner />}
      <div className="col-4 ps-1 pe-1">
        <div className="pg-price-total-heading">LEAD TIME</div>
        <div className="pg-price-total-heading">
          {leadTime && leadTime.imageUrl && (
            <div className="leadtime-image">
              <img
                src={leadTime.imageUrl}
                className="img-fluid"
                alt={leadTime.name}
              />
            </div>
          )}
          <span className="leadtime-summary">
            {maskPricingAndLeadTimes && <strong>N/A</strong>}
            {!maskPricingAndLeadTimes && leadTime &&
              <strong>
                <EffectiveLeadTimeLabel
                  isFastTrack={leadTime.isFastTrack}
                  leadTimeDays={leadTime.totalLeadTimeInDays}
                  showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
                  availableOn={leadTime.availableOn}
                />
              </strong>
            }
          </span>

        </div>
      </div>
      <PriceCalculationComponent title="MSRP" price={productCalculation.unitMsrp} showPricingSpinner={showPricingSpinner} maskPrice={maskPricingAndLeadTimes} />
      <PriceCalculationComponent title="D. NET" price={productCalculation.unitPrice} showPricingSpinner={showPricingSpinner} maskPrice={maskPricingAndLeadTimes} />

    </div>
  </div>
);

const ProductDimension = ({ dimension, noBucketSpecsAvailable }) => {

  if (dimension.type !== 'MultiLine') {
    var value = dimension.value;
    if (dimension.category === productGroupDimensionCategory.Width && noBucketSpecsAvailable) {
      value = 'TBD';
    }
    return (
      <SingleLineProductDimension value={value} />
    );
  } else {
    return <MultiLineProductDimension value={dimension.value} />;
  }
}


const SingleLineProductDimension = ({ value }) => {
  return <React.Fragment>{value}</React.Fragment>;
};

const MultiLineProductDimension = ({ value }) => {
  const items = value.split(newLineString).filter((x) => x);
  return (
    <ul className="mb-0">
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ul>
  );
};

const DimensionsComponent = ({ dimensions, productOptions, noBucketSpecsAvailable }) => {

  return (
    <div className="container ms-2 me-2">
      <div className="hr-divider inverse mb-1 mt-1">
        <h4 className="hr-divider-content hr-divider-heading">Model</h4>
      </div>
      {dimensions
        .filter((x) => !x.hideFromOnline)
        .map((dimension, idx) => (
          <div className="row" key={idx}>
            <div className="col-5 text-start">{dimension.name}</div>
            <div className="col-7 text-end">
              <ProductDimension dimension={dimension} noBucketSpecsAvailable={noBucketSpecsAvailable} />
            </div>
          </div>
        ))}
      {productOptions.length > 0 && (
        <React.Fragment>
          <div className="hr-divider inverse mb-1 mt-1">
            <h4 className="hr-divider-content hr-divider-heading">Options</h4>
          </div>
          {productOptions.map((productOption, idx) => (
            <div className="row" key={idx}>
              <div className="col-12">{productOption.optionName}</div>
            </div>
          ))}
        </React.Fragment>
      )}
    </div>
  );
};

const ProductGroupData = ({ productGroup, productConfiguration }) => {
  return (
    <React.Fragment>
      <div className="product-detail-title">{productGroup.name}</div>
      <div className="row justify-content-center pg-description">
        {productGroup.description}
      </div>
      <div className="row">
        <div className="col-12 d-flex justify-content-center pg-image">
          <DetailImage imageUrl={productGroup.imageUrl} />
        </div>
      </div>
      <div className="row justify-content-center">
        <ProductGroupMarketingComponent
          marketing={productGroup.marketing}
          productConfiguration={productConfiguration}
        />
      </div>
    </React.Fragment>
  );
};

const ProductDataComponent = makeConcealable(
  ({
    quoteProductDto,
    productOptionSets,
    onQuoteProductDtoChange,
    onSaveProduct,
    submitting,
    onCancel,
    isOrderFormMode,
    isAmiPriceLookupMode,
    submitEnabled,
    inventoryItem,
    visibilityProductGroup,
    showPricingSpinner,
    showLeadTimeCalculationSpinner
  }) => {
    // We map 'productOptionSets' instead of 'quoteProductDto.productOptions' to maintain the sets order.
    const productOptions = productOptionSets
      .filter((set) =>
        quoteProductDto.productOptions.some(
          (x) => !!x.productOptionId && x.productOptionSetId === set.id
        )
      )
      .map((set) => {
        const allSetOptions = set.productOptionsBySeries
          .map((x) => x.productOptions)
          .flat();
        const productOption = quoteProductDto.productOptions.find(
          (x) => x.productOptionSetId === set.id
        );
        return {
          setName: set.name,
          optionName: allSetOptions.find(
            (x) => x.id === productOption.productOptionId
          ).name,
        };
      });
    const isEditProductMode = !!quoteProductDto.id;
    const noBucketSpecsAvailable = quoteProductDto.detail && quoteProductDto.detail.noBucketSpecsAvailable();

    return (
      <Fragment>
        <div className="row justify-content-center pg-dimensions">
          <DimensionsComponent
            dimensions={quoteProductDto.dimensions}
            productOptions={productOptions}
            noBucketSpecsAvailable={noBucketSpecsAvailable}
          />
        </div>
        {!quoteProductDto.allOptionsConfigured && (
          <div className="text-warning pg-notconfigured">
            Please complete your option selection to view Price and Lead Time
          </div>
        )}
        {!visibilityProductGroup && (
          <div className="text-warning pg-notconfigured">
            Please contact AMI for pricing and lead time
          </div>
        )}
        {quoteProductDto.allOptionsConfigured && visibilityProductGroup && quoteProductDto && quoteProductDto.pricing && (
          <div className="row justify-content-center pg-price mt-1 mb-2">
            <PricesCalculationComponent
              leadTime={quoteProductDto.leadTime}
              productCalculation={quoteProductDto.pricing}
              showPricingSpinner={showPricingSpinner}
              showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
              showPricingAndLeadTimesBanner={quoteProductDto.manufacturingInstructions}
              maskPricingAndLeadTimes={quoteProductDto.manufacturingInstructions && !appConfig.isManagementApp}
            />
          </div>
        )}
        {!isAmiPriceLookupMode && inventoryItem && (
          <div className="row">
            {!inventoryItem.isConsignment && <div className="col d-flex justify-content-center"><div className="badge text-bg-secondary inventory-item-type">
              <span>{inventoryItem.inStock ? 'In Stock' : 'Stock Run'}</span>
            </div></div>}
            {inventoryItem.isConsignment && <div className='col'><div className="alert alert-info p-2 mb-0">Consignment</div></div>}
          </div>
        )}
        {!isAmiPriceLookupMode && (
          <div className="row justify-content-center pg-actions mt-2">
            <Fragment>
              {visibilityProductGroup && (
                <div className="col-5 quantity d-flex align-items-center">
                  <div className="me-2">
                    Quantity
                    {inventoryItem && <div>(max {inventoryItem.quantity})</div>}
                  </div>
                  <div>
                    <Input
                      type="number"
                      min="1"
                      value={quoteProductDto.quantity}
                      onChange={(e) => {
                        const quantity = window.parseInt(e.target.value);
                        if (
                          !inventoryItem ||
                          quantity <= inventoryItem.quantity
                        ) {
                          onQuoteProductDtoChange({ quantity });
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="col-7 text-end">
                <div className="row">
                  <div className="col">
                    {!isOrderFormMode && (
                      <button onClick={onCancel} className="btn btn-link p-2">
                        cancel
                      </button>
                    )}
                    {visibilityProductGroup && (
                      <Button
                        color="success"
                        onClick={onSaveProduct}
                        className="m-1"
                        disabled={!submitEnabled || submitting}
                      >
                        {submitting
                          ? isEditProductMode
                            ? 'Saving'
                            : 'Adding'
                          : isEditProductMode
                            ? 'Save'
                            : `Add to ${isOrderFormMode ? 'Order' : 'Quote'}`}
                        <Spinner show={submitting} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          </div>
        )}
        {isAmiPriceLookupMode && (
          <div className="row justify-content-center pg-actions mt-3">
            <div className="col-7 text-center">
              <Button color="success" onClick={onCancel} className="w-50 m-1">
                Done
              </Button>
            </div>
          </div>
        )}
      </Fragment>
    );
  }
);

export default class ProductDetailComponent extends React.PureComponent {
  static propTypes = {
    productGroup: PropTypes.instanceOf(Object).isRequired,
    submitting: PropTypes.bool.isRequired,
    quoteProductDto: PropTypes.instanceOf(QuoteProductDto).isRequired,
    onQuoteProductDtoChange: PropTypes.func.isRequired,
    onSaveProduct: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    scrollOnShow: PropTypes.bool.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    productOptionSets: PropTypes.arrayOf(Object).isRequired,
    isOrderFormMode: PropTypes.bool,
    isAmiPriceLookupMode: PropTypes.bool.isRequired,
    submitEnabled: PropTypes.bool,
    inventoryItem: PropTypes.instanceOf(Object),
    visibilityProductGroup: PropTypes.bool.isRequired,
    showPricingSpinner: PropTypes.bool.isRequired,
    showLeadTimeCalculationSpinner: PropTypes.bool.isRequired
  };

  static defaultProps = {
    submitEnabled: true,
  };

  render() {
    const {
      productGroup,
      quoteProductDto,
      productConfiguration,
      onQuoteProductDtoChange,
      onSaveProduct,
      onCancel,
      scrollOnShow,
      submitting,
      productOptionSets,
      isOrderFormMode,
      isAmiPriceLookupMode,
      submitEnabled,
      inventoryItem,
      visibilityProductGroup,
      showPricingSpinner,
      showLeadTimeCalculationSpinner
    } = this.props;
    return (
      <div className="container product-detail">
        <ProductGroupData
          productGroup={productGroup}
          productConfiguration={productConfiguration}
        />
        <ProductDataComponent
          show={!!quoteProductDto.productId}
          quoteProductDto={quoteProductDto}
          productOptionSets={productOptionSets}
          onQuoteProductDtoChange={onQuoteProductDtoChange}
          onSaveProduct={onSaveProduct}
          submitting={submitting}
          onCancel={onCancel}
          scrollOnShow={scrollOnShow}
          isOrderFormMode={isOrderFormMode}
          isAmiPriceLookupMode={isAmiPriceLookupMode}
          submitEnabled={submitEnabled}
          inventoryItem={inventoryItem}
          visibilityProductGroup={visibilityProductGroup}
          showPricingSpinner={showPricingSpinner}
          showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
        />
      </div>
    );
  }
}
